import { z } from 'zod';
import { newId } from '@quizee/events';
import {
  QUESTION_TYPES,
  type Format,
  type InteractionContent,
  type Option,
  type Outcome,
  type Question,
} from './content';
import { defaultPresentation, FORMAT_RULES, interactionSchema, type FormatRules } from './formats';

/**
 * The shape a model is asked to draft in, and how that becomes an interaction.
 *
 * This is deliberately not the content model. A drafted interaction carries no ids, no
 * presentation and no format field: the model writes prompts, labels and outcome titles, and
 * everything else is ours. Ids in particular are minted here with `newId`, because a model
 * inventing them produces duplicates and prefixes that do not match the id kind they sit in.
 *
 * It lives beside `newPoll` for the same reason that does: there is one definition of what a new
 * interaction is, and the drafting path comes through it rather than building content by hand.
 */

/**
 * Caps on what a model may draft in one go.
 *
 * These are below what the content model allows. A hundred question survey is a valid interaction
 * but not a sensible first draft, and every question the model writes is output tokens somebody
 * pays for. A publisher who wants more can add questions in the editor.
 */
export const DRAFT_MAX_QUESTIONS = 12;
export const DRAFT_MAX_OPTIONS = 6;
export const DRAFT_MAX_OUTCOMES = 6;

const draftedOutcomeSchema = z
  .object({
    /**
     * The model's own handle for this outcome, used to point options at it.
     *
     * Not an id. It never reaches storage: it is mapped to a minted `out_` id during assembly.
     */
    key: z.string().min(1).max(60),
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
  })
  .strict();

const draftedOptionSchema = z
  .object({
    label: z.string().min(1).max(300),
    is_correct: z.boolean().optional(),
    /**
     * An array rather than a map keyed by outcome, because a map with model chosen keys cannot be
     * described to a model as a closed schema, and a closed schema is what keeps the answer usable.
     */
    outcome_weights: z
      .array(
        z
          .object({
            outcome_key: z.string().min(1).max(60),
            weight: z.number().finite().gt(0).max(100),
          })
          .strict(),
      )
      .max(DRAFT_MAX_OUTCOMES)
      .optional(),
  })
  .strict();

const draftedQuestionSchema = z
  .object({
    type: z.enum(QUESTION_TYPES),
    prompt: z.string().min(1).max(1000),
    required: z.boolean().optional(),
    multiline: z.boolean().optional(),
    placeholder: z.string().max(200).optional(),
    scale: z
      .object({
        min: z.number().int().min(-1000).max(1000),
        max: z.number().int().min(-1000).max(1000),
        min_label: z.string().max(60).optional(),
        max_label: z.string().max(60).optional(),
      })
      .strict()
      .optional(),
    options: z.array(draftedOptionSchema).max(DRAFT_MAX_OPTIONS).optional(),
  })
  .strict();

/**
 * What a model hands back.
 *
 * Strict on purpose. A field we did not ask for means the model has answered a different question
 * from the one we put, and the retry that follows is cheaper than reasoning about what it meant.
 */
export const draftedInteractionSchema = z
  .object({
    title: z.string().min(1).max(300),
    introduction: z.string().max(2000).optional(),
    completion_message: z.string().max(2000).optional(),
    outcomes: z.array(draftedOutcomeSchema).max(DRAFT_MAX_OUTCOMES).optional(),
    questions: z.array(draftedQuestionSchema).min(1).max(DRAFT_MAX_QUESTIONS),
  })
  .strict();

export type DraftedInteraction = z.infer<typeof draftedInteractionSchema>;
export type DraftedQuestion = z.infer<typeof draftedQuestionSchema>;
export type DraftedOption = z.infer<typeof draftedOptionSchema>;

/**
 * Turn a drafted interaction into content, or throw.
 *
 * Ids are minted here. Presentation comes from the format, not the model. Fields the format has no
 * place for are dropped rather than passed through: a poll has no outcomes and no correct answers,
 * and carrying them would only fail validation for a reason the model cannot act on.
 *
 * The last line is the point of the whole function: nothing leaves here without passing
 * `interactionSchema`, which is the same check the editor and the publish path run.
 */
export function assembleDraft(format: Format, draft: DraftedInteraction): InteractionContent {
  const rules = FORMAT_RULES[format];

  const outcomeIds = new Map<string, string>();
  const outcomes: Outcome[] = [];

  if (rules.requiresOutcomes) {
    for (const drafted of draft.outcomes ?? []) {
      // A repeated key is the model naming one outcome twice. Keep the first and let the weights
      // pointing at that key resolve to it.
      if (outcomeIds.has(drafted.key)) continue;
      const outcomeId = newId('outcome');
      outcomeIds.set(drafted.key, outcomeId);
      outcomes.push({
        id: outcomeId,
        title: drafted.title,
        ...(drafted.description ? { description: drafted.description } : {}),
      });
    }
  }

  const content = {
    format,
    title: draft.title,
    ...(draft.introduction ? { introduction: draft.introduction } : {}),
    questions: draft.questions.map((question) => assembleQuestion(rules, question, outcomeIds)),
    ...(outcomes.length > 0 ? { outcomes } : {}),
    ...(draft.completion_message ? { completion_message: draft.completion_message } : {}),
    presentation: defaultPresentation(format),
  };

  return interactionSchema.parse(content);
}

function assembleQuestion(
  rules: FormatRules,
  drafted: DraftedQuestion,
  outcomeIds: ReadonlyMap<string, string>,
): Question {
  const choice = drafted.type === 'single_choice' || drafted.type === 'multiple_choice';
  const options = choice
    ? (drafted.options ?? []).map((option) => assembleOption(rules, option, outcomeIds))
    : [];

  return {
    id: newId('question'),
    type: drafted.type,
    prompt: drafted.prompt,
    // A draft that says nothing about this is asking a question it expects an answer to.
    required: drafted.required ?? true,
    options,
    ...(drafted.type === 'scale' && drafted.scale ? { scale: drafted.scale } : {}),
    ...(drafted.type === 'free_text' && drafted.placeholder !== undefined
      ? { placeholder: drafted.placeholder }
      : {}),
    ...(drafted.type === 'free_text' && drafted.multiline !== undefined
      ? { multiline: drafted.multiline }
      : {}),
  };
}

function assembleOption(
  rules: FormatRules,
  drafted: DraftedOption,
  outcomeIds: ReadonlyMap<string, string>,
): Option {
  const weights: Record<string, number> = {};

  if (rules.requiresOutcomes) {
    for (const weight of drafted.outcome_weights ?? []) {
      const outcomeId = outcomeIds.get(weight.outcome_key);
      // A weight pointing at an outcome the model never declared is dropped. If that leaves the
      // option pointing at nothing, `interactionSchema` refuses it, which is the retry we want.
      if (outcomeId !== undefined) weights[outcomeId] = weight.weight;
    }
  }

  return {
    id: newId('option'),
    label: drafted.label,
    ...(rules.requiresCorrectAnswers && drafted.is_correct !== undefined
      ? { is_correct: drafted.is_correct }
      : {}),
    ...(rules.requiresOutcomes && Object.keys(weights).length > 0
      ? { outcome_weights: weights }
      : {}),
  };
}

/**
 * The format's rules, written out for a model to read.
 *
 * Derived from FORMAT_RULES rather than written per format, so a new row in that table teaches the
 * drafting prompt about the new format without anybody remembering to come back here.
 */
export function formatBrief(format: Format): string {
  const rules = FORMAT_RULES[format];
  const maxQuestions = Math.min(rules.maxQuestions, DRAFT_MAX_QUESTIONS);
  const lines: string[] = [];

  lines.push(
    rules.minQuestions === maxQuestions
      ? `Write exactly ${maxQuestions} question.`
      : `Write between ${rules.minQuestions} and ${maxQuestions} questions.`,
  );
  lines.push(`Allowed question types: ${rules.questionTypes.map(humanType).join(', ')}.`);
  lines.push(
    `A single choice or multiple choice question needs between 2 and ${DRAFT_MAX_OPTIONS} options.`,
  );

  if (rules.requiresCorrectAnswers) {
    lines.push(
      'Every question must have a correct answer: set is_correct true on exactly one option of a single choice question, and on at least one option of a multiple choice question.',
    );
  } else {
    lines.push('There are no correct answers, so never set is_correct.');
  }

  if (rules.requiresOutcomes) {
    lines.push(
      `Declare between 2 and ${DRAFT_MAX_OUTCOMES} outcomes, each with a short key you invent and a title the reader will see.`,
    );
    lines.push(
      'Every option must carry at least one outcome weight, using an outcome_key you declared. Weights are positive numbers, roughly 1 to 5.',
    );
  } else {
    lines.push('This format has no outcomes, so never return outcomes or outcome weights.');
  }

  lines.push(
    rules.hasResults
      ? 'The reader is shown results at the end.'
      : 'The reader is shown no results, so write a short completion_message thanking them.',
  );

  return lines.map((line) => `- ${line}`).join('\n');
}

function humanType(type: string): string {
  return type.replace(/_/g, ' ');
}

/**
 * The tool input schema, narrowed to one format.
 *
 * Narrowing matters: a poll offered a `scale` question type or an `outcomes` array will eventually
 * use them, and every use is a round trip we pay for and refuse. What the model cannot describe it
 * cannot get wrong.
 */
export function draftToolSchema(format: Format): Record<string, unknown> {
  const rules = FORMAT_RULES[format];
  const maxQuestions = Math.min(rules.maxQuestions, DRAFT_MAX_QUESTIONS);
  const types = rules.questionTypes;
  const hasChoice = types.includes('single_choice') || types.includes('multiple_choice');

  const optionProperties: Record<string, unknown> = {
    label: { type: 'string', maxLength: 300, description: 'What the reader taps.' },
  };
  if (rules.requiresCorrectAnswers) {
    optionProperties['is_correct'] = {
      type: 'boolean',
      description: 'True on the option that is right.',
    };
  }
  if (rules.requiresOutcomes) {
    optionProperties['outcome_weights'] = {
      type: 'array',
      maxItems: DRAFT_MAX_OUTCOMES,
      description: 'How strongly this answer points at each outcome.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['outcome_key', 'weight'],
        properties: {
          outcome_key: { type: 'string', description: 'The key of an outcome you declared.' },
          weight: { type: 'number', minimum: 1, maximum: 100 },
        },
      },
    };
  }

  const questionProperties: Record<string, unknown> = {
    type: { type: 'string', enum: [...types] },
    prompt: { type: 'string', maxLength: 1000, description: 'The question the reader reads.' },
    required: { type: 'boolean', description: 'Whether the reader must answer. Defaults to true.' },
  };
  if (hasChoice) {
    questionProperties['options'] = {
      type: 'array',
      minItems: 2,
      maxItems: DRAFT_MAX_OPTIONS,
      description: 'Only for single choice and multiple choice questions.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['label'],
        properties: optionProperties,
      },
    };
  }
  if (types.includes('free_text')) {
    questionProperties['placeholder'] = {
      type: 'string',
      maxLength: 200,
      description: 'Only for free text questions. Greyed out hint text in the field.',
    };
    questionProperties['multiline'] = {
      type: 'boolean',
      description: 'Only for free text questions. True when the answer wants a paragraph.',
    };
  }
  if (types.includes('scale')) {
    questionProperties['scale'] = {
      type: 'object',
      additionalProperties: false,
      required: ['min', 'max'],
      description: 'Only for scale questions, for example 1 to 5.',
      properties: {
        min: { type: 'integer' },
        max: { type: 'integer' },
        min_label: { type: 'string', maxLength: 60 },
        max_label: { type: 'string', maxLength: 60 },
      },
    };
  }

  const properties: Record<string, unknown> = {
    title: {
      type: 'string',
      maxLength: 300,
      description: 'What the publisher will see it called.',
    },
    introduction: {
      type: 'string',
      maxLength: 2000,
      description: 'A sentence or two above the first question. Optional.',
    },
    questions: {
      type: 'array',
      minItems: rules.minQuestions,
      maxItems: maxQuestions,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['type', 'prompt'],
        properties: questionProperties,
      },
    },
  };

  if (rules.requiresOutcomes) {
    properties['outcomes'] = {
      type: 'array',
      minItems: 2,
      maxItems: DRAFT_MAX_OUTCOMES,
      description: 'The results a reader can land on.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['key', 'title'],
        properties: {
          key: { type: 'string', maxLength: 60, description: 'Your own short handle, not shown.' },
          title: { type: 'string', maxLength: 200 },
          description: { type: 'string', maxLength: 2000 },
        },
      },
    };
  }
  if (!rules.hasResults) {
    properties['completion_message'] = {
      type: 'string',
      maxLength: 2000,
      description: 'Shown after the reader finishes, since this format shows no results.',
    };
  }

  return {
    type: 'object',
    additionalProperties: false,
    required: rules.requiresOutcomes ? ['title', 'questions', 'outcomes'] : ['title', 'questions'],
    properties,
  };
}
